import React, { useState, useEffect } from 'react';
import { Line, Bar, Pie } from 'react-chartjs-2';
import axios from 'axios';
import GLOBALS from '../../utils/Globals';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
} from 'chart.js';
import 'chartjs-adapter-date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

const DashboardAnalytics = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const baseURLGetAllPDFDocs =
          process.env.REACT_APP_ENV === "PROD"
            ? GLOBALS.PROD.baseURLGetAllPDFDocs
            : GLOBALS.DEV.baseURLGetAllPDFDocs;

        const response = await axios.get(baseURLGetAllPDFDocs);
        setData(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '20px',
        color: '#666' 
      }}>
        Loading analytics...
      </div>
    );
  }

  // Process data for products over time
  const timelineData = {
    labels: data.map(item => new Date(item.metadata.timestamp)),
    datasets: [{
      label: 'Number of Products',
      data: data.map(item => item.metadata.product_count),
      fill: false,
      borderColor: 'rgb(75, 192, 192)',
      tension: 0.1
    }]
  };

  // Process data for user productivity
  const userProductivity = data.reduce((acc, item) => {
    const email = item.metadata.email;
    acc[email] = (acc[email] || 0) + (item.metadata.product_count || 0);
    return acc;
  }, {});

  const userProductivityData = {
    labels: Object.keys(userProductivity),
    datasets: [{
      label: 'Products Created',
      data: Object.values(userProductivity),
      backgroundColor: [
        'rgba(255, 99, 132, 0.5)',
        'rgba(54, 162, 235, 0.5)',
        'rgba(255, 206, 86, 0.5)',
        'rgba(75, 192, 192, 0.5)',
        'rgba(153, 102, 255, 0.5)',
      ],
      borderColor: [
        'rgba(255, 99, 132, 1)',
        'rgba(54, 162, 235, 1)',
        'rgba(255, 206, 86, 1)',
        'rgba(75, 192, 192, 1)',
        'rgba(153, 102, 255, 1)',
      ],
      borderWidth: 1
    }]
  };

  // Process data for daily product creation
  const dailyProducts = data.reduce((acc, item) => {
    const date = new Date(item.metadata.timestamp).toLocaleDateString();
    acc[date] = (acc[date] || 0) + (item.metadata.product_count || 0);
    return acc;
  }, {});

  const dailyProductsData = {
    labels: Object.keys(dailyProducts),
    datasets: [{
      label: 'Products per Day',
      data: Object.values(dailyProducts),
      backgroundColor: 'rgba(54, 162, 235, 0.5)',
      borderColor: 'rgba(54, 162, 235, 1)',
      borderWidth: 1
    }]
  };

  // Calculate total products
  const totalProducts = data.reduce((sum, item) =>
    sum + (item.metadata.product_count || 0), 0
  );

  // Process data for validation status — gtin_valid is a boolean true/false
  const validationStats = data.reduce((acc, item) => {
    if (item.products && Array.isArray(item.products)) {
      item.products.forEach(product => {
        const isValid = product.validation?.gtin_valid;
        if (isValid === true) acc['Valid'] = (acc['Valid'] || 0) + 1;
        else if (isValid === false) acc['Invalid'] = (acc['Invalid'] || 0) + 1;
        else acc['Not Validated'] = (acc['Not Validated'] || 0) + 1;
      });
    }
    return acc;
  }, {});

  const validationColorMap = {
    'Valid': 'rgba(75, 192, 192, 0.5)',
    'Invalid': 'rgba(255, 99, 132, 0.5)',
    'Not Validated': 'rgba(255, 206, 86, 0.5)',
  };
  const validationBorderMap = {
    'Valid': 'rgba(75, 192, 192, 1)',
    'Invalid': 'rgba(255, 99, 132, 1)',
    'Not Validated': 'rgba(255, 206, 86, 1)',
  };
  const validationLabels = Object.keys(validationStats);
  const validationData = {
    labels: validationLabels,
    datasets: [{
      label: 'Validation Status',
      data: validationLabels.map(k => validationStats[k]),
      backgroundColor: validationLabels.map(k => validationColorMap[k] || 'rgba(153, 102, 255, 0.5)'),
      borderColor: validationLabels.map(k => validationBorderMap[k] || 'rgba(153, 102, 255, 1)'),
      borderWidth: 1
    }]
  };

  // Calculate total validated products
  const totalValidated = validationStats['Valid'] || 0;
  const totalInvalid = validationStats['Invalid'] || 0;
  const totalNotValidated = validationStats['Not Validated'] || 0;

  // GTIN validation uses the same data as validationStats
  const gtinValidationStats = validationStats;

  const gtinColorMap = {
    'Valid': 'rgba(75, 192, 192, 0.5)',
    'Invalid': 'rgba(255, 99, 132, 0.5)',
    'Not Validated': 'rgba(255, 206, 86, 0.5)',
  };
  const gtinBorderMap = {
    'Valid': 'rgba(75, 192, 192, 1)',
    'Invalid': 'rgba(255, 99, 132, 1)',
    'Not Validated': 'rgba(255, 206, 86, 1)',
  };
  const gtinLabels = Object.keys(gtinValidationStats);
  const gtinValidationData = {
    labels: gtinLabels,
    datasets: [{
      label: 'GTIN Validation Status',
      data: gtinLabels.map(k => gtinValidationStats[k]),
      backgroundColor: gtinLabels.map(k => gtinColorMap[k] || 'rgba(153, 102, 255, 0.5)'),
      borderColor: gtinLabels.map(k => gtinBorderMap[k] || 'rgba(153, 102, 255, 1)'),
      borderWidth: 1
    }]
  };

  // Process data for products per email over time
  const processEmailTimelineData = (data) => {
    // Create a map of email -> array of {timestamp, count} objects
    const emailData = data.reduce((acc, item) => {
      const email = item.metadata.email;
      const timestamp = new Date(item.metadata.timestamp);
      const count = item.metadata.product_count || 0;

      if (!acc[email]) {
        acc[email] = [];
      }
      
      acc[email].push({ timestamp, count });
      return acc;
    }, {});

    // Get unique timestamps across all entries
    const allTimestamps = [...new Set(data.map(item => 
      new Date(item.metadata.timestamp).toISOString()
    ))].sort();

    // Create datasets for each email
    const datasets = Object.entries(emailData).map(([email, points], index) => ({
      label: email,
      data: allTimestamps.map(timestamp => {
        const point = points.find(p => 
          p.timestamp.toISOString() === timestamp
        );
        return point ? point.count : 0;
      }),
      fill: false,
      borderColor: [
        'rgb(75, 192, 192)',
        'rgb(255, 99, 132)',
        'rgb(255, 206, 86)',
        'rgb(153, 102, 255)',
        'rgb(54, 162, 235)',
      ][index % 5],
      tension: 0.1
    }));

    return {
      labels: allTimestamps.map(t => new Date(t)),
      datasets
    };
  };

  const emailTimelineData = processEmailTimelineData(data);

  return (
    <div className="dashboard-analytics">
      <div className="analytics-header" style={{
        textAlign: 'center',
        marginBottom: '20px',
        padding: '20px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ color: '#333', marginBottom: '10px' }}>Product Analytics Dashboard</h2>
        <p style={{ color: '#666' }}>Total Products Created: {totalProducts}</p>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: '20px',
          marginTop: '10px' 
        }}>
          <span style={{ color: '#4CAF50' }}>Valid: {totalValidated}</span>
          <span style={{ color: '#f44336' }}>Invalid: {totalInvalid}</span>
          <span style={{ color: '#ff9800' }}>Not Validated: {totalNotValidated}</span>
        </div>
      </div>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '20px',
        marginBottom: '20px' 
      }}>
        <div style={{ 
          backgroundColor: 'white',
          padding: '15px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ marginBottom: '15px', color: '#666' }}>Products Created Over Time</h3>
          <Line
            data={timelineData}
            options={{
              responsive: true,
              scales: {
                x: {
                  type: 'time',
                  time: {
                    unit: 'day'
                  },
                  title: {
                    display: true,
                    text: 'Date'
                  }
                },
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Number of Products'
                  }
                }
              }
            }}
          />
        </div>

        <div style={{ 
          backgroundColor: 'white',
          padding: '15px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ marginBottom: '15px', color: '#666' }}>Products by User</h3>
          <Pie
            data={userProductivityData}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'bottom'
                }
              }
            }}
          />
        </div>

        <div style={{ 
          backgroundColor: 'white',
          padding: '15px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ marginBottom: '15px', color: '#666' }}>Daily Product Creation</h3>
          <Bar
            data={dailyProductsData}
            options={{
              responsive: true,
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Number of Products'
                  }
                }
              }
            }}
          />
        </div>

        <div style={{ 
          backgroundColor: 'white',
          padding: '15px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ marginBottom: '15px', color: '#666' }}>Validation Status Distribution</h3>
          <Pie
            data={validationData}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'bottom'
                },
                tooltip: {
                  callbacks: {
                    label: function(context) {
                      const label = context.label || '';
                      const value = context.raw || 0;
                      const total = Object.values(validationStats).reduce((a, b) => a + b, 0);
                      const percentage = ((value / total) * 100).toFixed(1);
                      return `${label}: ${value} (${percentage}%)`;
                    }
                  }
                }
              }
            }}
          />
        </div>

        <div style={{ 
          backgroundColor: 'white',
          padding: '15px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ marginBottom: '15px', color: '#666' }}>GTIN Validation Status</h3>
          <Bar
            data={gtinValidationData}
            options={{
              responsive: true,
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Number of Products'
                  }
                },
                x: {
                  title: {
                    display: true,
                    text: 'Validation Status'
                  }
                }
              },
              plugins: {
                tooltip: {
                  callbacks: {
                    label: function(context) {
                      const value = context.raw || 0;
                      const total = Object.values(gtinValidationStats).reduce((a, b) => a + b, 0);
                      const percentage = ((value / total) * 100).toFixed(1);
                      return `Count: ${value} (${percentage}%)`;
                    }
                  }
                },
                legend: {
                  display: false
                }
              }
            }}
          />
          <div style={{ 
            marginTop: '15px', 
            display: 'flex', 
            justifyContent: 'center', 
            gap: '20px',
            flexWrap: 'wrap'
          }}>
            <span style={{ color: '#4CAF50' }}>
              Valid: {gtinValidationStats['Valid'] || 0}
            </span>
            <span style={{ color: '#f44336' }}>
              Invalid: {gtinValidationStats['Invalid'] || 0}
            </span>
            <span style={{ color: '#ff9800' }}>
              Not Validated: {gtinValidationStats['Not Validated'] || 0}
            </span>
          </div>
        </div>

        <div style={{ 
          backgroundColor: 'white',
          padding: '15px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '20px'
        }}>
          <h3 style={{ marginBottom: '15px', color: '#666' }}>Products Created by User Over Time</h3>
          <Line
            data={emailTimelineData}
            options={{
              responsive: true,
              scales: {
                x: {
                  type: 'time',
                  time: {
                    unit: 'day'
                  },
                  title: {
                    display: true,
                    text: 'Date'
                  }
                },
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Number of Products'
                  }
                }
              },
              plugins: {
                tooltip: {
                  mode: 'index',
                  intersect: false
                },
                legend: {
                  position: 'bottom',
                  labels: {
                    boxWidth: 12
                  }
                }
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default DashboardAnalytics;
